import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, PartyPopper, ArrowRight, LayoutDashboard } from 'lucide-react';

interface OnboardingCompleteProps {
  formId: string | null;
}

// Confetti colors
const CONFETTI_COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];

export function OnboardingComplete({ formId }: OnboardingCompleteProps) {
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Hide confetti after animation
    const timer = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, type: 'spring' }}
        className="max-w-md w-full"
      >
        <Card className="text-center shadow-lg">
          <CardContent className="pt-8 pb-6">
            {/* Success icon with animation */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <Check className="w-10 h-10 text-green-600" />
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <PartyPopper className="w-6 h-6 text-yellow-500" />
                <h2 className="text-2xl font-bold text-gray-900">You're all set!</h2>
                <PartyPopper className="w-6 h-6 text-yellow-500 transform scale-x-[-1]" />
              </div>

              <p className="text-gray-600 mb-8">
                Your form is ready to receive submissions. Start collecting feedback from your
                users!
              </p>

              <div className="space-y-3">
                {formId && (
                  <Button onClick={() => navigate(`/app/forms/${formId}`)} className="w-full">
                    View Your Form
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                <Button variant="outline" onClick={() => navigate('/app')} className="w-full">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Go to Dashboard
                </Button>
              </div>
            </motion.div>
          </CardContent>
        </Card>

        {/* Confetti effect */}
        {showConfetti && (
          <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
            {[...Array(60)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-3 h-3"
                style={{
                  background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                  left: `${Math.random() * 100}%`,
                  borderRadius: Math.random() > 0.5 ? '50%' : '0%',
                  rotate: `${Math.random() * 360}deg`,
                }}
                initial={{
                  y: -20,
                  x: 0,
                  opacity: 1,
                  scale: Math.random() * 0.5 + 0.5,
                }}
                animate={{
                  y: window.innerHeight + 100,
                  x: (Math.random() - 0.5) * 200,
                  opacity: 0,
                  rotate: Math.random() * 720 - 360,
                }}
                transition={{
                  duration: 2.5 + Math.random() * 2,
                  delay: Math.random() * 0.8,
                  ease: 'easeOut',
                }}
              />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
